import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Autocomplete, TextField, Box, IconButton,
  Typography, FormControl, Grid, Collapse
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import useDraggable from '../hooks/useDraggable';
import VolatileStatusesSelect from './VolatileStatusesSelect';

const PokemonDialog = ({ open, onClose, position, onSelectPokemon, teamData = [], excludeNames = [], currentPokemon = null }) => {
  const [selectedPokemon, setSelectedPokemon] = useState(null);
  const [selectedVolatileStatuses, setSelectedVolatileStatuses] = useState([]);
  const [statChanges, setStatChanges] = useState({ hp: 100, atk: 0, def: 0, spa: 0, spd: 0, spe: 0, acc: 0, eva: 0 });
  const [statsExpanded, setStatsExpanded] = useState(false);
  const [volatileStatuses, setVolatileStatuses] = useState([]);

  useEffect(() => {
    fetch(process.env.PUBLIC_URL + '/volatile.txt')
      .then(res => res.text())
      .then(text => setVolatileStatuses(text.trim().split('\n').map(s => s.trim()).filter(Boolean)))
      .catch(err => console.error('Error fetching volatile statuses:', err));
  }, []);

  useEffect(() => {
    if (open) {
      setSelectedPokemon(currentPokemon?.name || null);
      setSelectedVolatileStatuses(currentPokemon?.volatileStatuses || []);
      setStatChanges(currentPokemon?.stats || { hp: 100, atk: 0, def: 0, spa: 0, spd: 0, spe: 0, acc: 0, eva: 0 });
      setStatsExpanded(false);
    }
  }, [open]);

  const { ref, style, handleMouseDown, isDragging } = useDraggable({ resetOnClose: true });

  const positionName = (pos) => {
    switch (pos) {
      case 'topLeft':    return "Your Left Pokémon";
      case 'topRight':   return "Your Right Pokémon";
      case 'bottomLeft': return "Opponent's Left Pokémon";
      case 'bottomRight':return "Opponent's Right Pokémon";
      default:           return "Select Pokémon";
    }
  };

  const hasTeam = teamData.length > 0;

  // Available options: non-fainted team members
  const teamOptions = teamData.filter(p => p?.name && !p.fainted && p.revealed && !excludeNames.includes(p.name)).map(p => p.name);

  const handleSelect = () => {
    if (!selectedPokemon) return;
    const member = teamData.find(p => p.name === selectedPokemon) || {};
    onSelectPokemon(position, {
      name: selectedPokemon,
      // Only in-battle dynamic state — everything else comes from the team via backend merge
      volatileStatuses: selectedVolatileStatuses,
      stats: statChanges,
      filterStats: false,
    });
    onClose();
  };

  const handleClear = () => {
    setSelectedPokemon(null);
    setSelectedVolatileStatuses([]);
    setStatChanges({ hp: 100, atk: 0, def: 0, spa: 0, spd: 0, spe: 0, acc: 0, eva: 0 });
  };

  const clamp = (v, min, max) => Math.min(Math.max(Number(v), min), max);

  const statField = (label, key, min, max) => (
    <TextField
      type="number"
      label={label}
      value={statChanges[key]}
      onChange={e => setStatChanges(prev => ({ ...prev, [key]: Number(e.target.value) }))}
      onBlur={e => setStatChanges(prev => ({ ...prev, [key]: clamp(e.target.value, min, max) }))}
      variant="outlined"
      fullWidth
      InputLabelProps={{ style: { color: 'white' } }}
      inputProps={{ min, max, step: 1, style: { color: 'white' } }}
      sx={{ mt: 1, backgroundColor: '#221FC7', borderRadius: 1 }}
    />
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        ref,
        style: { ...style, backgroundColor: '#221FC7', transition: 'none' },
        onMouseDown: handleMouseDown,
        sx: {
          '& .MuiDialogTitle-root': {
            cursor: isDragging ? 'grabbing' : 'grab',
            userSelect: 'none',
            padding: '16px 24px',
            color: 'white',
          }
        }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {positionName(position)}
          <IconButton onClick={onClose} sx={{ color: 'white' }} aria-label="close dialog">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {!hasTeam ? (
            <Typography sx={{ color: 'rgba(255,255,255,0.6)', textAlign: 'center', py: 2 }}>
              Select a team first to pick an active Pokémon.
            </Typography>
          ) : (
            <>
              {/* Pokemon picker — only team members */}
              <Autocomplete
                options={teamOptions}
                value={selectedPokemon}
                onChange={(_, newValue) => setSelectedPokemon(newValue)}
                renderInput={(params) => (
                  <TextField {...params} label="Pokémon" variant="outlined" fullWidth />
                )}
                sx={{
                  '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: 'white' } },
                  '& .MuiSvgIcon-root': { color: 'white' },
                  mt: 1,
                }}
              />

              {selectedPokemon && (
                <>
                  {/* Volatile statuses (go away on switch-out) */}
                  <FormControl fullWidth sx={{ mt: 2 }}>
                    <VolatileStatusesSelect
                      options={volatileStatuses}
                      selectedStatuses={selectedVolatileStatuses}
                      setSelectedStatuses={setSelectedVolatileStatuses}
                    />
                  </FormControl>

                  {/* Stat boosts / HP */}
                  <Box
                    sx={{ mt: 2, display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                    onClick={() => setStatsExpanded(v => !v)}
                  >
                    <Typography component="p" variant="h6" color="white" sx={{ flexGrow: 1 }}>
                      Stats
                    </Typography>
                    <IconButton
                      sx={{ color: 'white', transform: statsExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
                      aria-label="expand stats"
                    >
                      <ExpandMoreIcon />
                    </IconButton>
                  </Box>

                  <Collapse in={statsExpanded} timeout="auto" unmountOnExit>
                    <Box sx={{ mt: 1, p: 2, border: '2px solid #ffffff', borderRadius: 2 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          {statField('HP', 'hp', 0, 100)}
                          {statField('ATK', 'atk', -6, 6)}
                          {statField('DEF', 'def', -6, 6)}
                          {statField('ACC', 'acc', -6, 6)}
                        </Grid>
                        <Grid item xs={6}>
                          {statField('SPA', 'spa', -6, 6)}
                          {statField('SPD', 'spd', -6, 6)}
                          {statField('SPE', 'spe', -6, 6)}
                          {statField('EVA', 'eva', -6, 6)}
                        </Grid>
                      </Grid>
                    </Box>
                  </Collapse>
                </>
              )}
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClear} variant="outlined" color="warning" sx={{ mr: 'auto' }}>
          Clear
        </Button>
        <Button onClick={onClose} variant="contained" color="error">Cancel</Button>
        <Button
          onClick={handleSelect}
          variant="containedSuccess"
          color="success"
          disabled={!selectedPokemon}
        >
          Select
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PokemonDialog;
